import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Radio } from 'lucide-react'
import { toast } from 'sonner'
import type { SocialTrackerItem } from '@digital-twins/shared-types'
import type {
  SocialMediaTabProps,
  TrackerAction,
} from '@/types/components/social-media-tab'
import { Heading } from '@/components/ui/Heading'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { SocialTrackerCard } from './SocialTrackerCard'
import { SocialTrackerDialog } from './SocialTrackerDialog'
import { SocialTrackerDrawer } from './SocialTrackerDrawer'
import { useSocialListeningApi } from './SocialListeningApiContext'

function errorMessage(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? fallback
  )
}

export function SocialMediaTab(props: SocialMediaTabProps) {
  const {
    showHeader = true,
    trackers,
    canManage,
    isLoading,
    openTrackerToken,
    onOpenTrackerChange,
    onTrackerChanged,
    onTrackerDeleted,
    onRefresh,
    canAddTracker = true,
    showRunDiagnostics = false,
    canAsk = true,
    showActionsInline = false,
  } = props

  const { t } = useTranslation('app')
  const socialListeningApi = useSocialListeningApi()

  const [busyToken, setBusyToken] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingTracker, setEditingTracker] =
    useState<SocialTrackerItem | null>(null)
  const [deletingTracker, setDeletingTracker] =
    useState<SocialTrackerItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const openTracker =
    trackers.find((tracker) => tracker.token === openTrackerToken) ?? null

  async function runTrackerAction(
    tracker: SocialTrackerItem,
    action: TrackerAction,
  ) {
    setBusyToken(tracker.token)
    try {
      const updated =
        action === 'sync'
          ? await socialListeningApi.syncTracker(tracker.token)
          : action === 'pause'
            ? await socialListeningApi.pauseTracker(tracker.token)
            : await socialListeningApi.resumeTracker(tracker.token)
      onTrackerChanged(updated)
      toast.success(
        action === 'sync'
          ? t('socialListening.toast.syncStarted')
          : action === 'pause'
            ? t('socialListening.toast.paused')
            : t('socialListening.toast.resumed'),
      )
    } catch (error: unknown) {
      toast.error(errorMessage(error, t('socialListening.toast.actionFailed')))
    } finally {
      setBusyToken(null)
    }
  }

  async function handleConfirmDelete() {
    if (!deletingTracker) {
      return
    }
    const token = deletingTracker.token
    setIsDeleting(true)
    try {
      await socialListeningApi.deleteTracker(token)
      onTrackerDeleted(token)
      if (openTrackerToken === token) {
        onOpenTrackerChange(null)
      }
      toast.success(t('socialListening.toast.deleted'))
      setDeletingTracker(null)
    } catch (error: unknown) {
      toast.error(errorMessage(error, t('socialListening.toast.actionFailed')))
    } finally {
      setIsDeleting(false)
    }
  }

  function handleAdd() {
    setEditingTracker(null)
    setIsDialogOpen(true)
  }

  function handleEdit(tracker: SocialTrackerItem) {
    setEditingTracker(tracker)
    setIsDialogOpen(true)
  }

  const addButton =
    canManage && canAddTracker ? (
      <Button
        type="button"
        variant="cta"
        shape="pill"
        className="h-9 px-4"
        data-testid="social-add-tracker"
        onClick={handleAdd}
      >
        <Plus className="mr-2 h-4 w-4" />
        {t('socialListening.header.addTracker')}
      </Button>
    ) : null

  const body = isLoading ? (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-[18px]">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-64 rounded-2xl" />
      ))}
    </div>
  ) : trackers.length === 0 ? (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-border/60 bg-muted/50 p-6 text-center dark:border-border dark:bg-card sm:p-10">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background text-muted-foreground dark:bg-muted">
        <Radio className="h-5 w-5" aria-hidden="true" />
      </div>
      <div
        className="flex max-w-lg flex-col items-center gap-2"
        data-testid="social-empty-state"
      >
        <Heading level={3} className="font-sans text-lg font-medium">
          {t('socialListening.empty.title')}
        </Heading>
        <Text size="sm" color="muted">
          {canManage
            ? t('socialListening.empty.body')
            : t('socialListening.empty.bodyMember')}
        </Text>
      </div>
      {canManage && canAddTracker ? addButton : null}
    </div>
  ) : (
    <div
      className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-[18px]"
      data-testid="social-tracker-list"
    >
      {trackers.map((tracker) => (
        <SocialTrackerCard
          key={tracker.token}
          tracker={tracker}
          canManage={canManage}
          isBusy={busyToken === tracker.token}
          onOpen={onOpenTrackerChange}
          onSync={(item) => void runTrackerAction(item, 'sync')}
          onTogglePause={(item) =>
            void runTrackerAction(
              item,
              item.status === 'paused' ? 'resume' : 'pause',
            )
          }
          onEdit={handleEdit}
          onDelete={setDeletingTracker}
          canAsk={canAsk}
          showActionsInline={showActionsInline}
        />
      ))}
    </div>
  )

  return (
    <section className="flex flex-col gap-4" data-testid="social-media-tab">
      {showHeader ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {canAsk ? (
            <Text size="sm" color="muted" className="max-w-[640px]">
              {t('socialListening.header.body')}
            </Text>
          ) : null}
          {trackers.length > 0 ? addButton : null}
        </div>
      ) : null}

      {body}

      <SocialTrackerDialog
        open={isDialogOpen}
        tracker={editingTracker}
        onOpenChange={setIsDialogOpen}
        onSaved={onTrackerChanged}
      />

      <SocialTrackerDrawer
        tracker={openTracker}
        canManage={canManage}
        onClose={() => onOpenTrackerChange(null)}
        onPostsRemoved={onRefresh}
        showRunDiagnostics={showRunDiagnostics}
        canAsk={canAsk}
      />

      <AlertDialog
        open={deletingTracker != null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingTracker(null)
          }
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('socialListening.delete.title')}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="social-delete-body">
              {t('socialListening.delete.body', {
                name: deletingTracker?.name ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">
              {t('socialListening.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive text-primary-dark-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              data-testid="social-delete-confirm"
              onClick={(event) => {
                event.preventDefault()
                void handleConfirmDelete()
              }}
            >
              {isDeleting
                ? t('socialListening.delete.deleting')
                : t('socialListening.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
